// src/pages/tour-packages/hooks/useTourPackageCreatePage.ts

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { getAuth, request } from "@/api/client";
import { PATHS } from "@/api/constants";
import { listTourTypes } from "@/api/features/tour-types/api";
import { listSuppliers } from "@/api/features/suppliers/api";

import { useCreateTourPackage } from "./useCreateTourPackage";
import { useTourPackageCreateForm } from "./useTourPackageCreateForm";

import type { CreateTourWithDetailsPayload } from "@/api/features/tours";

/* =========================================
 * Local option types (shape only)
 * ======================================= */

type TourTypeOption = {
  id: number;
  name: string;
};

type SupplierOption = {
  id: number;
  name: string;
};

/* =========================================
 * Main page hook
 * ======================================= */

export function useTourPackageCreatePage() {
  const auth = getAuth();
  const user = auth?.user as any;
  const roleName = String(user?.roleName ?? "").trim();
  const isSupplier = roleName === "Supplier";
  const supplierId = Number(user?.supplierId ?? 0);
  const supplierName =
    String(
      user?.supplierCompanyName ??
        user?.username ??
        "My Supplier Account"
    ).trim() || "My Supplier Account";

  /* -----------------------------
   * 1) Load master data
   * --------------------------- */

  const {
    data: tourTypesData,
    isLoading: isLoadingTourTypes,
    error: tourTypesError,
  } = useQuery({
    queryKey: ["tour-types", "create-page", isSupplier ? "supplier" : "admin"],
    queryFn: async () => {
      if (isSupplier) {
        const res = await request<any>({
          method: "GET",
          url: `${PATHS.tours.base}/public/filters`,
        });
        const data = res?.data ?? res;
        const types = Array.isArray(data?.tourTypes) ? data.tourTypes : [];
        return {
          items: types.map((tt: any) => ({
            id: Number(tt?.id),
            name: String(tt?.name ?? ""),
          })),
        };
      }

      return listTourTypes({ page: 1, limit: 100 });
    },
  });

  const {
    data: suppliersData,
    isLoading: isLoadingSuppliers,
    error: suppliersError,
  } = useQuery({
    queryKey: ["suppliers", "create-page", isSupplier ? supplierId : "all"],
    queryFn: async () => {
      if (isSupplier) {
        return {
          items:
            supplierId > 0
              ? [{ id: supplierId, supplierCompanyName: supplierName }]
              : [],
        };
      }
      return listSuppliers({
        page: 1,
        limit: 100,
        includeDeleted: false,
      });
    },
  });

  const tourTypeOptions: TourTypeOption[] = useMemo(
    () =>
      (tourTypesData?.items ?? []).map((tt: any) => ({
        id: tt.id,
        name: tt.name,
      })),
    [tourTypesData?.items]
  );

  const supplierOptions: SupplierOption[] = useMemo(
    () =>
      (suppliersData?.items ?? []).map((s: any) => ({
        id: s.id,
        name: s.supplierCompanyName ?? s.companyName ?? "",
      })),
    [suppliersData?.items]
  );

  const isInitializing = isLoadingTourTypes || isLoadingSuppliers;
  const initialLoadError = tourTypesError ?? suppliersError ?? null;

  /* -----------------------------
   * 2) Create mutation
   * --------------------------- */

  const [saveError, setSaveError] = useState<Error | null>(null);

  const createTourMutation = useCreateTourPackage({
    onSuccess: () => setSaveError(null),
    onError: (error) => setSaveError(error),
  });

  /* -----------------------------
   * 3) Wire up form hook
   * --------------------------- */

  const formState = useTourPackageCreateForm({
    defaultValues:
      isSupplier && supplierId > 0
        ? {
            tour: {
              supplierId,
            } as any,
          }
        : undefined,
    onSubmit: async (payload: CreateTourWithDetailsPayload) => {
      await createTourMutation.mutateAsync(payload);
    },
  });

  /* -----------------------------
   * 4) Expose everything for page
   * --------------------------- */

  return {
    // from useTourPackageCreateForm:
    ...formState,

    // master data for dropdowns (in case page needs it)
    tourTypes: tourTypeOptions,
    suppliers: supplierOptions,
    supplierLocked: isSupplier,

    // loading / error for initial master-data fetch
    isInitializing,
    initialLoadError,

    // mutation state
    isSaving: createTourMutation.isPending,
    saveError,
  };
}
